<script setup lang="ts">
import type { MutationStatus } from "@tanstack/vue-query";
import { computed, useId, useTemplateRef } from "vue";

const {
  deletingStatus,
  disabled = false,
  disabledReason = undefined,
} = defineProps<{
  deletingStatus: MutationStatus;
  disabled?: boolean;
  disabledReason?: string;
}>();

const emit = defineEmits<{
  confirmed: [];
  initiated: [];
}>();

const dialog = useTemplateRef("dialog");
const reasonId = useId();
// Every button needs its own anchor name, otherwise all tooltips in a table
// would resolve to the same anchor.
const anchorName = `--delete-button-${reasonId}`;

const showsReason = computed(() => disabled && disabledReason !== undefined);

const open = () => {
  // We want the button to stay focusable for accessibility, so clicks still fire here.
  if (disabled) return;

  emit("initiated");
  dialog.value?.showModal();
};

const close = () => {
  dialog.value?.close();
};
</script>

<template>
  <button
    :class="$attrs.class"
    type="button"
    :aria-disabled="disabled || undefined"
    :aria-describedby="showsReason ? reasonId : undefined"
    :style="showsReason ? { anchorName } : undefined"
    @click="open()"
  >
    <slot />
  </button>
  <div
    v-if="showsReason"
    :id="reasonId"
    role="tooltip"
    class="disabled-reason"
    :style="{ positionAnchor: anchorName }"
  >
    {{ disabledReason }}
  </div>
  <dialog ref="dialog">
    <template v-if="deletingStatus == 'error'">
      <p class="error">Etwas ist schiefgelaufen.</p>
      <footer class="row row-end">
        <button class="button" @click="close()">Ok</button>
      </footer>
    </template>
    <template v-else>
      <p><slot name="confirmation" /></p>
      <footer class="row row-end">
        <button class="button button--secondary" @click="close()">
          Abbrechen
        </button>
        <button
          class="button"
          :aria-busy="deletingStatus == 'pending'"
          @click="$emit('confirmed')"
        >
          Löschen
        </button>
      </footer>
    </template>
  </dialog>
</template>

<style scoped>
.disabled-reason {
  display: none;
  position: absolute;
  position-area: block-start span-inline-start;
  margin-block-end: var(--spacing-xs);
  max-inline-size: var(--inline-size-sm);
  background-color: var(--bg-color-tooltip);
  border-radius: var(--border-radius-tooltip);
  padding: var(--padding-tooltip);
  box-shadow: var(--shadow);
}

button:hover + .disabled-reason,
button:focus-visible + .disabled-reason {
  display: block;
}
</style>
